import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × MONET: Impressionistic data — perception and variance
// Diffused color, pixelized light, blended geometry
export function VasarelyMonetMode({ brand }: ModeRendererProps) {
  return (
    <g>
      {/* Soft impressionist background */}
      <defs>
        <radialGradient id="monet-atmosphere" cx="0.5" cy="0.4">
          <stop offset="0%" stopColor={brand.aqua} stopOpacity="0.2" />
          <stop offset="50%" stopColor={brand.violet} stopOpacity="0.12" />
          <stop offset="100%" stopColor={brand.deep} stopOpacity="0.05" />
        </radialGradient>
      </defs>

      <rect x="0" y="0" width="794" height="1123" fill="url(#monet-atmosphere)" />

      <g transform="translate(397 540)">
        {/* Pixelized light field (impressionist perception) */}
        {Array.from({ length: 40 }).map((_, i) =>
          Array.from({ length: 50 }).map((_, j) => {
            const x = (i - 19.5) * 16;
            const y = (j - 24.5) * 16;
            const dist = Math.sqrt(x * x + y * y);
            const diffusion = Math.sin(dist * 0.03) * 8;
            const colors = [brand.aqua, brand.violet, brand.amber, brand.paper];
            const colorIndex = Math.floor((i + j + diffusion) % 4);
            const variance = Math.random() * 0.15;
            
            return (
              <circle
                key={`${i}-${j}`}
                cx={x + diffusion}
                cy={y - diffusion * 0.5}
                r="6"
                fill={colors[colorIndex]}
                opacity={0.15 + variance}
              />
            );
          })
        )}

        {/* Soft water lily circles (data pools) */}
        {Array.from({ length: 8 }).map((_, i) => {
          const angle = (i * Math.PI) / 4;
          const r = 180;
          const x = Math.cos(angle) * r;
          const y = Math.sin(angle) * r;
          
          return (
            <g key={i}>
              <ellipse
                cx={x}
                cy={y}
                rx="55"
                ry="45"
                fill={brand.aqua}
                opacity="0.15"
              />
              <ellipse
                cx={x}
                cy={y}
                rx="40"
                ry="32"
                fill={brand.violet}
                opacity="0.12"
              />
              <ellipse
                cx={x}
                cy={y}
                rx="25"
                ry="20"
                fill={brand.amber}
                opacity="0.18"
              />
            </g>
          );
        })}

        {/* Blended optical waves */}
        {Array.from({ length: 15 }).map((_, i) => {
          const r = 50 + i * 20;
          const distortion = Math.sin(i * 0.4) * 12;
          
          return (
            <ellipse
              key={i}
              cx={distortion}
              cy={-distortion * 0.3}
              rx={r}
              ry={r * (1 + Math.sin(i * 0.3) * 0.15)}
              fill="none"
              stroke={i % 3 === 0 ? brand.aqua : i % 3 === 1 ? brand.violet : brand.amber}
              strokeWidth="2"
              opacity={0.2 - i * 0.008}
            />
          );
        })}

        {/* Atmospheric light patches */}
        {Array.from({ length: 50 }).map((_, i) => {
          const angle = Math.random() * Math.PI * 2;
          const r = Math.random() * 280;
          const x = Math.cos(angle) * r;
          const y = Math.sin(angle) * r;
          const size = 8 + Math.random() * 12;
          
          return (
            <ellipse
              key={i}
              cx={x}
              cy={y}
              rx={size}
              ry={size * 0.7}
              fill={Math.random() > 0.5 ? brand.aqua : brand.violet}
              opacity={0.1 + Math.random() * 0.15}
            />
          );
        })}

        {/* Diffused geometry grid */}
        {Array.from({ length: 12 }).map((_, i) =>
          Array.from({ length: 12 }).map((_, j) => {
            const x = (i - 5.5) * 44;
            const y = (j - 5.5) * 44;
            const blur = Math.sin(i * 0.5 + j * 0.5) * 6;
            
            return (
              <rect
                key={`${i}-${j}`}
                x={x - 12 + blur}
                y={y - 12 - blur}
                width="24"
                height="24"
                fill={brand.white}
                opacity={0.08}
              />
            );
          })
        )}

        {/* Central perception focus */}
        <circle cx="0" cy="0" r="100" fill="none" stroke={brand.aqua} strokeWidth="2" opacity="0.25" />
        <circle cx="0" cy="0" r="70" fill={brand.violet} opacity="0.1" />
      </g>

      {/* Impressionist note */}
      <text x="397" y="1020" textAnchor="middle" fontFamily="serif" fontSize="11" fill={brand.aqua} opacity="0.5" fontStyle="italic">
        impression, données
      </text>
    </g>
  );
}
